import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import classNames from "classnames";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { Button } from "@design-system/Button/Button";
import {
    Entity,
    EntityProfileRevenueBracket,
    EntityProfileType,
} from "@common/types/entity";
import { DEFAULT_WORKSPACE_TIMEZONE } from "@common/workspaceTimezone";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ProfileFormSchema } from "@components/onboarding/useEntityProfileFormConfig";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { VStack } from "@design-system/VStack/VStack";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import fieldSpacing from "../components/fieldSpacing.module.scss";
import animation from "../layout/OnboardingLayout.module.scss";
import { EntityProfileFormFields } from "../components/EntityProfileFormFields";
import { WorkspaceTimezoneField } from "../components/WorkspaceTimezoneField";
import { profileFormSchema } from "../useEntityProfileFormConfig";
import { useUserDisplayNames } from "../useUserDisplayNames";
import { DEFAULT_COUNTRY_CODE } from "../constants";
import styles from "./Profile.module.scss";
import commonStyles from "./common.module.scss";

const fields = createFormikFields<ProfileFormSchema>();

interface ProfileUser {
    firstName: string | null;
    lastName: string | null;
    unverifiedPhoneNumber: string | null;
    preferredName: string | null;
}

interface Props {
    entity: Entity;
    user: ProfileUser;
    onSubmit: (values: ProfileFormSchema) => Promise<void>;
    onBack: () => void;
    onValuesChange?: (values: ProfileFormSchema) => void;
}

export const Profile: React.FC<Props> = ({
    entity,
    user,
    onSubmit,
    onBack,
    onValuesChange,
}) => {
    const workspace = useActiveWorkspace();
    const isFreshbooksCustomer = workspace?.isFreshbooksCustomer ?? false;
    const [loading, setLoading] = useState(false);

    const { firstName, lastName } = useUserDisplayNames(user, entity);

    const formik = useFormik<ProfileFormSchema>({
        initialValues: {
            firstName: firstName ?? "",
            lastName: lastName ?? "",
            source: isFreshbooksCustomer
                ? "Freshbooks"
                : (entity.profile?.source ?? ""),
            unverifiedPhoneNumber: user.unverifiedPhoneNumber ?? "",
            name: entity.name ?? "",
            legalType: entity.legalType ?? EntityProfileType.NO_LEGAL_ENTITY,
            industries: entity.profile?.industries ?? [],
            industryOther: entity.profile?.industryOther,
            annualRevenue:
                entity.profile?.annualRevenue ??
                EntityProfileRevenueBracket.LESS_THAN_50K,
            country: entity.profile?.country ?? DEFAULT_COUNTRY_CODE,
            timezone: DEFAULT_WORKSPACE_TIMEZONE,
        },
        validate: validateFormikWithZodSchema(profileFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: onSubmit,
        }),
    });

    // Report form value changes to parent for sidebar preview
    useEffect(() => {
        onValuesChange?.(formik.values);
    }, [formik.values, onValuesChange]);

    return (
        <div className={commonStyles.content420}>
            <header
                className={classNames(
                    animation.fadeIn2,
                    styles.headerContainer,
                )}
            >
                <h1 className={classNames(commonStyles.title, styles.header)}>
                    Create your Kick account
                </h1>
                {isFreshbooksCustomer && (
                    <h2 className={styles.subHeader}>
                        Your first step to free 2025 bookkeeping
                    </h2>
                )}
            </header>

            <FormikProvider value={formik}>
                <Form className={animation.fadeIn1}>
                    <GridRow gap="24px">
                        <GridCol sm={6}>
                            <fields.Text
                                name="firstName"
                                label="First name"
                                size="xl"
                                autoComplete="given-name"
                                className={fieldSpacing.inputField}
                            />
                        </GridCol>

                        <GridCol sm={6}>
                            <fields.Text
                                name="lastName"
                                label="Last name"
                                size="xl"
                                autoComplete="family-name"
                                className={fieldSpacing.inputField}
                            />
                        </GridCol>
                    </GridRow>

                    <EntityProfileFormFields isOnboardingFlow />

                    <WorkspaceTimezoneField name="timezone" />

                    <VStack as="footer" align="center" className="mt-4">
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            loading={loading}
                            block
                            disabled={!formik.isValid}
                            size="xl"
                            rightIcon={<EnterKeyHint />}
                        >
                            Next
                        </ButtonWithLoader>
                        <Button
                            variant="secondary"
                            disabled={loading}
                            type="button"
                            onClick={onBack}
                            className="mt-4"
                            size="xl"
                        >
                            Back
                        </Button>
                    </VStack>
                </Form>
            </FormikProvider>
        </div>
    );
};
